/**
 * useCurrentOfficer
 *
 * Resolves the logged-in officer's Officer-entity ID (not the User ID).
 * Dispatches `fetchOfficerProfile(userId)` once on mount and exposes the
 * current officer profile + id. Used by Phase-2 officer-scoped pages.
 *
 * @author Civilhealth Development Team
 */
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { fetchOfficerProfile } from '../store/slices/officerSlice';
import { RootState, AppDispatch } from '../store';

export const useCurrentOfficer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const currentOfficer = useSelector((state: RootState) => state.officer.currentOfficer);
  const loading = useSelector((state: RootState) => state.officer.loading);
  const [searchParams] = useSearchParams();

  // Allow staff / office roles to act on behalf of an officer via ?officerId=N.
  // Officers themselves have no such param, so behavior is unchanged for them.
  const overrideId = searchParams.get('officerId');
  const overrideNumeric = overrideId ? Number(overrideId) : undefined;
  const overrideValid = overrideNumeric != null && Number.isFinite(overrideNumeric) && overrideNumeric > 0;

  useEffect(() => {
    if (overrideValid) return; // acting on behalf — no self profile fetch needed
    if (user?.id && !currentOfficer) {
      dispatch(fetchOfficerProfile(user.id) as any);
    }
  }, [dispatch, user?.id, currentOfficer, overrideValid]);

  return {
    officerId: (overrideValid ? overrideNumeric : currentOfficer?.id) as number | undefined,
    officer: currentOfficer,
    loading,
    actingOnBehalf: overrideValid,
  };
};

export default useCurrentOfficer;